import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content from 'components/base/Content'
import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Link, { Theme as LinkTheme } from 'components/base/Link'
import Title from 'components/base/Title'
import IconGrid from 'components/base/Icon/Grid'
import { Theme as IconTheme } from 'components/base/Icon/Grid/Icon'
import whyPickImg from 'assets/images/products/WhyPick.svg'
import Img from 'components/base/Img'
import CtaTwoCol from 'components/blocks/CtaTwoCol'
import InsuranceTypeIcon, { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'
import StartQuoteButton from 'components/base/StartQuoteButton'

const ProductsAutoInsurance: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Auto Insurance | Insurance Lounge'
      description='In need of an auto insurance policy that doesn’t break the bank? Insurance Lounge can help you find the best rate on auto insurance. Start your free quote today!'
      ogImg='/images/og-images/Auto.webp'
    />
    <PageHero
      cta={true}
      ctaTitle='Get an auto insurance quote now.'
      theme={PageHeroTheme.auto}
      trackingInfo={{ page_category: 'product_page_auto' }}
    >
      Auto Insurance
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <InsuranceTypeIcon theme={InsuranceTypeIconTheme.auto} colored={true} hasMargin={true} />
      <Paragraph theme={ParagraphTheme.introLarge}>
        Auto insurance is required by law for drivers in almost every state. Plus, having insurance can save you time and money if you get into an accident, damage your car, or have other unforeseen vehicular issues. With so many different auto insurance options, it can be challenging to figure out which one is the best fit for you. If you’re looking for a lower rate or are a new driver, Insurance Lounge is here to help.
      </Paragraph>
    </Content>
    <Content margined={true} center={true}>
      <Title level={2}>What Do I Need to Complete My Auto Insurance Quote?</Title>
      <IconGrid
        icons={[
          {
            theme: IconTheme.license,
            title: 'A valid driver’s license',
          },
          {
            theme: IconTheme.makeAndModel,
            title: 'The make, model, and year of your car',
          },
          {
            theme: IconTheme.mileage,
            title: 'The car’s mileage',
          },
          {
            theme: IconTheme.zip,
            title: 'Your ZIP code',
          },
        ]}
      />
    </Content>
    <CtaTwoCol>
      <Content>
        <Title level={2}>Why Pick Insurance lounge for your Insurance Needs?</Title>
        <Paragraph>Trust the experts at <Link to='/about-us'>Insurance Lounge</Link> to provide you with a quick and efficient auto insurance experience. We don’t charge commissions, and our Insurance Concierges<sup>®</sup> are available 24/7 to answer your questions and provide a quote. <Link to='/quote/new'>Get a free quote online</Link> or call us at (541) 479-4975 today to get started.</Paragraph>
        <Link theme={LinkTheme.button} to='/contact'>Connect with our Insurance Concierges</Link>
      </Content>
      <Content>
        <Img src={whyPickImg} src2x={undefined} alt='Why Pick insurance Lounge?' />
      </Content>
    </CtaTwoCol>
    <Content margined={true} center={true}>
      <Title level={2}>What Affects My Auto Insurance Rates?</Title>
      <IconGrid
        icons={[
          IconTheme.age,
          IconTheme.drivingRecord,
          IconTheme.creditHistory,
          IconTheme.gender,
          IconTheme.makeAndModel,
          IconTheme.highRisk,
          IconTheme.maritalStatus,
          IconTheme.zip,
        ]}
      />
    </Content>
    <Content short={true} margined={true} center={true}>
      <Title level={2}>Why Should You Compare Auto Insurance Quotes?</Title>
      <Paragraph>
        There are many different kinds of <Link to='/products/auto-insurance'>auto insurance</Link> coverage. The most common policies include liability, collision, and comprehensive coverage. By understanding which car insurance will meet your needs, you’ll be more equipped to compare premiums and deductibles. This can differ based on your location and the coverage you choose. By comparing car insurance quotes, you can save a significant amount of time and money in the long run.
      </Paragraph>
      <Paragraph>
        <StartQuoteButton
          trackingInfo={{
            button_position: 'bottom',
            page_category: 'product_page_auto',
          }}
        >
          Start Your Quote
        </StartQuoteButton>
      </Paragraph>
    </Content>
  </div>
)

export default ProductsAutoInsurance
